import * as React from "react";
import {useEffect, useState} from "react";
import {FormControl, Grid, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {enUS, ptBR} from "date-fns/locale";
import GenericDialog from "../../../../../components/dialog/generic-dialog.component";
import {translate} from "../../../../../translate/translate";
import {BaseService} from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import {URLS as PAGE_URLS} from "../../../../../services/routes";
import {Client} from "../../../../../models/basic/client";
import {Project} from "../../../../../models/basic/project";
import {Template} from "../../../../../models/npi/template";
import {Npi} from "../../../../../models/npi/npi";
import {toast} from "react-toastify";
import { ErrorComponent } from "../../../../../components/error/error.component";
import { useNavigate } from "react-router-dom";

interface NewNpiDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
}

const isValidDate = (date: Date | null): date is Date => {
    return date !== null && !isNaN(date.getTime());
};

const toLocalIsoDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
};

const menuProps = {
    PaperProps: {
        style: { maxHeight: 300 },
    },
};


const NewNpiDialogComponent: React.FC<NewNpiDialogProps> = ({ open, onClose, onCreated }) => {
    const clientService = new BaseService<Client>(URLS.CLIENT);
    const projectService = new BaseService<Project>(URLS.PROJECT);
    const templateService = new BaseService<Template>(URLS.TEMPLATE);
    const npiService = new BaseService<Npi>(URLS.NPI);

    const [clients, setClients] = useState<Client[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);

    const [client, setClient] = useState<string>("");
    const [project, setProject] = useState<string>("");
    const [template, setTemplate] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const locale = translate.language === "pt" ? ptBR : enUS;

    useEffect(() => {
        if (!open) {
            return;
        }
        clientService.clearParameters();
        clientService.addParameter("active", "true");
        clientService.getAll().then(response => setClients(response.data));

        templateService.clearParameters();
        templateService.addParameter("active", "true");
        templateService.getAll().then(response => setTemplates(response.data));
    }, [open]);

    useEffect(() => {
        setProject("");
        setProjects([]);
        if (!client) {
            return;
        }
        projectService.clearParameters();
        projectService.addParameter("client", client);
        projectService.addParameter("active", "true");
        projectService.getAll().then(response => setProjects(response.data));
    }, [client]);

    useEffect(() => {
        if (!template) {
            setStartDate(null);
        }
    }, [template]);

    useEffect(() => {
        if (!template || !isValidDate(startDate)) {
            setEndDate(null);
            return;
        }
        npiService.clearParameters();
        npiService.addParameter("template", template);
        npiService.addParameter("start_date", toLocalIsoDate(startDate));
        npiService.getAllFromListRoute("calculate-end-date").then((response: any) => {
            setEndDate(new Date(`${response.data.end_date}T00:00:00`));
        });
    }, [template, startDate]);

    const handleClose = () => {
        setClient("");
        setProject("");
        setTemplate("");
        setStartDate(null);
        setEndDate(null);
        onClose();
    };

    const navigate = useNavigate();
    

    const handleSubmit = () => {
        if (!isValidDate(startDate)) {
            return;
        }
        npiService.clearParameters();
        npiService.save({
            project: Number(project),
            template: Number(template),
            start_date: toLocalIsoDate(startDate),
        } as Npi).then(() => {
            toast.success(translate.t("successfully_registered"));
            onCreated?.();
            handleClose();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    return (
        <GenericDialog
            open={open}
            onClose={handleClose}
            title={"new_npi"}
            colorButton={"primary"}
            labelButton={"save_form"}
            closeButtonText={"cancel"}
            disableConfirm={!project || !template || !isValidDate(startDate)}
            handleConfirm={handleSubmit}>
            <Grid container spacing={2} width="100%">
                <Grid size={12}>
                    <FormControl fullWidth>
                        <InputLabel id="npi-client-label" required>{translate.t("client")}</InputLabel>
                        <Select
                            MenuProps={menuProps}
                            labelId="npi-client-label"
                            label={`${translate.t("client")}*`}
                            value={client}
                            onChange={(event) => setClient(event.target.value)}>
                            {clients.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth disabled={!client}>
                        <InputLabel id="npi-project-label" required>{translate.t("project")}</InputLabel>
                        <Select
                            MenuProps={menuProps}
                            labelId="npi-project-label"
                            label={`${translate.t("project")}*`}
                            value={project}
                            onChange={(event) => setProject(event.target.value)}>
                            {projects.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth>
                        <InputLabel id="npi-template-label" required>{translate.t("planning_template")}</InputLabel>
                        <Select
                            MenuProps={menuProps}
                            labelId="npi-template-label"
                            label={`${translate.t("planning_template")}*`}
                            value={template}
                            sx={{
                                "& .MuiTypography-root": {
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap"
                                }
                            }}
                            onChange={(event) => setTemplate(event.target.value)}>
                            {templates.map((item) => (
                                <MenuItem key={item.id} value={item.id} sx={{maxWidth: "552px"}}>
                                    <Typography sx={{
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        width: "100%"
                                    }}>{item.name}</Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                        <DatePicker
                            label={translate.t("start_date")}
                            value={startDate}
                            onChange={(date) => setStartDate(date as Date)}
                            disabled={!template}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    required: true,
                                    error: startDate !== null && !isValidDate(startDate),
                                    helperText: startDate !== null && !isValidDate(startDate) ? translate.t("invalid_date_format") : undefined,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                        <DatePicker
                            label={translate.t("end_date")}
                            value={endDate}
                            disabled
                            slotProps={{textField: {fullWidth: true}}}
                        />
                    </LocalizationProvider>
                </Grid>
            </Grid>
        </GenericDialog>
    );
};

export default NewNpiDialogComponent;
